import { Box, Grid, keyframes } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Heading, MinorHeading, MinorTitle } from '../shared/typography/PrimaryTypography';
import PrimaryButton from '../shared/button/PrimaryButton';
import GhostButton from '../shared/button/GhostButton';
import aeeAward2026 from '../../assets/landing_page/AEE_AWARD_2026.svg';
import saee_award_2025 from '../../assets/landing_page/SAEE_AWARD_2025.svg';
import sanea_award from '../../assets/landing_page/SANEA_AWARD.svg';
import syntiro_logo_slogan from '../../assets/syntiro-logo-horizontal-white-text-slogan-text-only.svg';
import syntiro_animation from '../../assets/videos/syntiro-logo-animation.gif';
import PrimaryDivider from '../shared/decoration/PrimaryDivider';
import InfiniteCarousel from '../shared/carousel/InfiniteCarousel';

interface HeroBannerProps {
  handleDisplayLoginModal: (display: boolean) => void;
}

const key_metrics = [
  {
    header_metric: '800',
    subtext: (
      <>
        Sites monitored
        <br />
        across 80 clients
      </>
    )
  },
  {
    header_metric: '38%',
    subtext: (
      <>
        Reduction in
        <br />
        energy intensity
      </>
    )
  },
  {
    header_metric: 'R900m',
    subtext: (
      <>
        Saved through utility
        <br />
        tariff projects
      </>
    )
  },
  {
    header_metric: '518',
    subtext: (
      <>
        Energy projects
        <br />
        identified
      </>
    )
  },
  {
    header_metric: '25%',
    subtext: (
      <>
        Less refrigeration
        <br />
        energy used
      </>
    )
  },
  { header_metric: '100 000+', subtext: 'Data points monitored' }
];

const subtle_flow = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fade_in = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const HeroBanner = ({ handleDisplayLoginModal }: HeroBannerProps) => {
  const navigate = useNavigate();

  const metrics_to_render = key_metrics.map((metric) => (
    <Box key={metric.header_metric} sx={{ mx: 4, my: 1 }}>
      <Heading weight="Bold" sx={{ color: '#00AB97', textAlign: 'center' }}>
        {metric.header_metric}
      </Heading>
      <MinorHeading weight="Bold" sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.85)' }}>
        {metric.subtext}
      </MinorHeading>
    </Box>
  ));

  return (
    <Box
      id="home"
      sx={{
        background:
          'linear-gradient(270deg, #061420, #0E3A5A, #0c3848, #093340, #0d3d52, #0E3A5A, #061420)',
        backgroundSize: '400% 400%',
        animation: `${subtle_flow} 22s ease infinite`,
        display: 'flex',
        flexDirection: 'column',
        py: { xs: 4, sm: 5, lg: 6 }
      }}
    >
      <Grid container spacing={8} sx={{ animation: `${fade_in} 1s linear` }}>
        <Grid
          size={12}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={syntiro_animation} style={{ width: '25%', maxWidth: 160 }} alt="" />
            <img src={syntiro_logo_slogan} alt="Syntiro" style={{ width: '70%', maxWidth: 600 }} />
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 2, mt: 2 }}>
            <img
              src={aeeAward2026}
              alt="AEE Award 2026"
              style={{ height: 75, filter: 'drop-shadow(0 0 8px rgba(0,171,151,0.5))' }}
            />
            <img
              src={saee_award_2025}
              alt="SAEE Award 2025"
              style={{ height: 75, filter: 'drop-shadow(0 0 8px rgba(0,171,151,0.5))' }}
            />
            <img
              src={sanea_award}
              alt="SANEA Award"
              style={{ height: 75, filter: 'drop-shadow(0 0 8px rgba(0,171,151,0.5))' }}
            />
          </Box>
        </Grid>

        <Grid size={12}>
          <Grid
            container
            spacing={0}
            sx={{ justifyContent: 'center', alignItems: 'center', px: { xs: 2, sm: 4 } }}
          >
            <Grid size={{ xs: 12, sm: 5 }} sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
              <MinorTitle weight="Bold" dark sx={{ fontSize: '3rem', lineHeight: 1.3 }}>
                Real sustainability.
                <br />
                In real time.
                <br />
                For real impact.
              </MinorTitle>
            </Grid>
            <Grid size={{ xs: 12, sm: 'auto' }} sx={{ display: 'flex', justifyContent: 'center' }}>
              <PrimaryDivider orientation="vertical" sx={{ height: 250, mx: 6 }} opacity={0.2} />
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                sx={{
                  background: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(4px)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 3,
                  overflow: 'hidden',
                  width: '100%'
                }}
              >
                <InfiniteCarousel
                  width="100%"
                  height={200}
                  animation_duration={20}
                  items={metrics_to_render}
                  direction="left"
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          size={12}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            mt: { xs: -6, sm: -6, lg: 0 },
            pb: { xs: 2, sm: 3, lg: 4 }
          }}
        >
          <GhostButton outlined sx={{ minWidth: '200px' }} onClick={() => navigate('/contact')}>
            Request a Demo
          </GhostButton>
          <PrimaryButton
            onClick={() => handleDisplayLoginModal(true)}
            sx={{ minWidth: '200px', display: { xs: 'none', lg: 'block' } }}
          >
            Client Login
          </PrimaryButton>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HeroBanner;
